import React from "react";
import { Col, Row } from "react-bootstrap";
import {
  isUserExpired,
  isUserSignedUpAndConsented,
  LessonRecording,
} from "../common/api";
import { ApiContext } from "../common/context/ApiContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { AccountContext } from "../common/context/AccountContext";
import { useLiveData } from "../common/utils";
import { default as UUID } from "uuid";
import {
  AnalyticsContext,
  ViewLessonRecordingsEvent,
} from "../common/context/AnalyticsContext";
import { PageContainer } from "../common/CommonStyles";
import { LessonRecordingItem } from "../components/LessonRecordingItem";
import { Paywall } from "./Paywall";
import { useTranslation } from "react-i18next";
import { LessonRecordingEditMenu } from "../components/LessonRecordingEditMenu";

type LessonRecordingsState = {
  recordings: LessonRecording[] | null;
};

export const LessonRecordingsPage: React.FC = () => {
  const [state, setState] = React.useState<LessonRecordingsState>({
    recordings: null,
  });
  const [canAccessReplays, setCanAccessReplays] = React.useState<Boolean>(true);

  const { getLessonRecordings } = React.useContext(ApiContext);
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const { accountLiveData } = React.useContext(AccountContext);
  const discoverFetchedForProfileStateVersion = React.useRef<number | null>(
    null
  );
  const fetchJobForProfileStateVersion = React.useRef<number | null>(null);
  const currentFetchJobUuid = React.useRef<string | null>(null);
  const timeoutId = React.useRef<number>();
  const { logEvent } = React.useContext(AnalyticsContext);
  const editMenuAnchorRef = React.useRef<HTMLButtonElement | null>(null);
  const [isEditMenuOpen, setIsEditMenuOpen] = React.useState(false);
  const [recordingToEdit, setRecordingToEdit] =
    React.useState<LessonRecording | null>(null);
  const { t } = useTranslation();

  React.useEffect(() => {
    logEvent(new ViewLessonRecordingsEvent());
  }, [logEvent]);

  const maybeRefetchLessonRecordings = React.useCallback(async () => {
    const account = accountLiveData.value;
    if (account.hasMaybeProfileState()) {
      if (
        account.profileStateCurrentVersion !==
          discoverFetchedForProfileStateVersion.current &&
        account.profileStateCurrentVersion !==
          fetchJobForProfileStateVersion.current
      ) {
        startLoading();
        const fetchJobUuid = (currentFetchJobUuid.current = UUID.v4());
        fetchJobForProfileStateVersion.current =
          account.profileStateCurrentVersion;
        const lessonRecordings = await getLessonRecordings();
        if (currentFetchJobUuid.current !== fetchJobUuid) {
          return;
        }
        setCanAccessReplays(
          isUserSignedUpAndConsented(account.user) &&
            !isUserExpired(account.user)
        );
        setState({ recordings: lessonRecordings });
        discoverFetchedForProfileStateVersion.current =
          fetchJobForProfileStateVersion.current;
        stopLoading();
        timeoutId.current = window.setTimeout(() => {
          currentFetchJobUuid.current = UUID.v4();
          discoverFetchedForProfileStateVersion.current = null;
          fetchJobForProfileStateVersion.current = null;
          maybeRefetchLessonRecordings();
        }, 8 * 60 * 60 * 1000); // TODO: Do we need to refresh every 8 hrs?
      }
    }
  }, [accountLiveData, getLessonRecordings, startLoading, stopLoading]);

  useLiveData(maybeRefetchLessonRecordings, accountLiveData);

  if (!canAccessReplays) {
    return <Paywall />;
  }
  if (!state.recordings) {
    return <></>;
  }

  return (
    <PageContainer>
      <Row>
        <h2 className="mb-3">{t("lesson_replays_title")}</h2>
        {state.recordings
          .filter((r) => r.profile_id === accountLiveData.value?.profile?.id)
          .map((recording) => (
            <Col key={recording.id} xs={12} lg={6} className="mb-3">
              <LessonRecordingItem
                recording={recording}
                onMenuButtonClick={(event) => {
                  editMenuAnchorRef.current = event.currentTarget;
                  setRecordingToEdit(recording);
                  setIsEditMenuOpen(true);
                }}
              />
            </Col>
          ))}
      </Row>
      <LessonRecordingEditMenu
        anchor={editMenuAnchorRef.current!}
        isOpen={isEditMenuOpen}
        onClose={() => setIsEditMenuOpen(false)}
        onLessonRecordingDeleted={(recording) => {
          let recordings = state.recordings;
          if (recordings !== null) {
            recordings.splice(recordings.indexOf(recording), 1);
            setState({ recordings: recordings });
          }
        }}
        recording={recordingToEdit}
      />
    </PageContainer>
  );
};
